import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunkAction } from "../../../redux-store";
import { moveCargo } from "../../dispatchActions";
import {
  cargoViewsDecoded,
  loadUndispatchedCargoViews,
} from "../../data/cargo-views";
import { updateSmartMatch } from "../../cargo";
import { deleteCargo, openCargoFoundDeleted } from "../cargo-dialog";
import * as selectors from "./selectors";
import { SortOrder, SortProperty, FilterType } from "./types";
import uniq from "lodash/uniq";
import { setFeatures } from "../../features";
import { userStorage } from "../../../helpers/user-storage";

const prefix = "app/dispatching";

// Saved per user and restored once the user is known (see checkAuthState).
export const DISPATCHING_VIEW_PAGE_TEAMS_FILTER =
  "dispatchingViewPageTeamsFilter";
export const DISPATCHING_VIEW_PAGE_CARGO_TAG_FILTER =
  "dispatchingViewPageCargoTagFilter";
export const DISPATCHING_FILTER_TYPES: readonly FilterType[] = [
  "cargos",
  "routes",
];

const UNDISPATCHED_SORTING = "undispatchedSorting";

type State = {
  originalUnassignedCargoIds: string[];
  saving: boolean;
  unassignedCargoSortingValueSet: boolean;
  unassignedCargoSortingNew: {
    property: SortProperty;
    order: SortOrder;
  }[];
  teamFilterOptions: Record<FilterType, string[]>;
  cargoTagFilterIds: string[];
  truckFilterIds: string[];
  trailerFilterIds: string[];
  driverFilterIds: string[];
  unassignedCargoSearchText: string;
  dragState?: {
    cargoId: string;
    sourceRouteId: string | null;
    originalIndex: number;
    currentIndex: number;
    currentRouteId: null | string;
    routeCargoIds: Record<string, string[]>;
    committing: boolean;
  };
  routeSearchDates?: {
    fromDate: string;
    toDate: string;
  };
};

const unassignedCargoSortingNewFromStorage =
  localStorage.getItem(UNDISPATCHED_SORTING);
const unassignedCargoSortingNew = unassignedCargoSortingNewFromStorage
  ? (JSON.parse(unassignedCargoSortingNewFromStorage) as any)
  : [
      {
        property: "Læssedato",
        order: "ASC",
      },
    ];

const initialState: State = {
  saving: false,
  teamFilterOptions: { cargos: [], routes: [] },
  cargoTagFilterIds: [],
  truckFilterIds: [],
  trailerFilterIds: [],
  driverFilterIds: [],
  originalUnassignedCargoIds: [],
  unassignedCargoSortingValueSet: false,
  unassignedCargoSortingNew,
  unassignedCargoSearchText: "",
};

export const commit = (): AppThunkAction => (dispatch, getState) => {
  const state = getState().app.dispatching.dragState;
  if (!state) {
    return;
  }
  const {
    cargoId,
    sourceRouteId,
    currentRouteId,
    currentIndex,
    originalIndex,
    committing,
  } = state;
  const destIndex =
    sourceRouteId === currentRouteId && currentIndex > originalIndex
      ? currentIndex + 1
      : currentIndex;
  if (committing) {
    return;
  }
  dispatch(slice.actions.setCommitting(true));
  return dispatch(
    moveCargo({
      sourceRouteId,
      cargoId,
      destIndex,
      destRouteId: currentRouteId,
    })
  ).finally(() => {
    dispatch(slice.actions.setCommitting(false));
  });
};

export const cancelDrag = (): AppThunkAction => (dispatch) => {
  dispatch(endDrag());
};

export const hover =
  (payload: {
    destRouteId: null | string;
    hoverCargoId?: string;
    position?: "ABOVE" | "BELOW";
  }): AppThunkAction =>
  (dispatch, getState) => {
    const globalState = getState();
    const state = globalState.app.dispatching;
    if (!state.dragState) {
      return;
    }
    const { currentRouteId: sourceRouteId, cargoId } = state.dragState;
    const { destRouteId, hoverCargoId, position } = payload;
    const sourceRoute = [
      ...selectors.selectRouteCargoIds(sourceRouteId)(globalState),
    ];
    if (destRouteId === sourceRouteId && !position) {
      return;
    }
    if (destRouteId === sourceRouteId) {
      return;
    }
    let destRoute =
      sourceRouteId === destRouteId
        ? sourceRoute
        : [...selectors.selectRouteCargoIds(destRouteId)(globalState)];
    // const destIndex = (() => {
    //   if (!hoverCargoId || !position) {
    //     return 0;
    //   }
    //   return destRoute.indexOf(hoverCargoId) + (position === "BELOW" ? 1 : 0);
    // })();
    const sourceIndex = sourceRoute.indexOf(cargoId);
    destRoute.splice(destRoute.length, 0, cargoId);
    sourceRoute.splice(sourceIndex, 1);
    // if (sourceRoute === destRoute) {
    //   if (sourceIndex === destIndex) {
    //     return;
    //   }
    //   if (
    //     sourceIndex ===
    //     destIndex + (position === "ABOVE" ? -1 : position === "BELOW" ? 1 : 0)
    //   ) {
    //     return;
    //   }
    // }
    // if (sourceIndex > destIndex) {
    //   sourceRoute.splice(sourceIndex, 1);
    //   destRoute.splice(destIndex, 0, cargoId);
    // } else {
    //   destRoute.splice(destIndex, 0, cargoId);
    //   sourceRoute.splice(sourceIndex, 1);
    // }
    dispatch(
      slice.actions.update({
        cargoId,
        routes: {
          [sourceRouteId || ""]: sourceRoute,
          [destRouteId || ""]: uniq(destRoute),
        },
        currentIndex: destRoute.indexOf(cargoId),
        currentRouteId: destRouteId,
      })
    );
  };

export const setFilterOptionsAndSave =
  (payload: {
    teamId: string;
    enabled: boolean;
    filterType: FilterType;
  }): AppThunkAction =>
  (dispatch, getState) => {
    dispatch(slice.actions.setFilterOptions(payload));

    const state = getState();
    const teamFilterOptions = state.app.dispatching.teamFilterOptions;
    userStorage.setItem(
      DISPATCHING_VIEW_PAGE_TEAMS_FILTER,
      JSON.stringify(teamFilterOptions)
    );
  };

export const endDrag = createAction(`${prefix}/end-drag`);

export const clear = createAction(`${prefix}/clear`);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    setRouteSearchDates: (
      state,
      action: PayloadAction<{ fromDate: string; toDate: string }>
    ) => {
      state.routeSearchDates = action.payload;
    },
    clearRouteSearchDates: (state) => {
      delete state.routeSearchDates;
    },
    setCommitting: (state, action: PayloadAction<boolean>) => {
      if (state.dragState) {
        state.dragState.committing = action.payload;
      }
    },
    setFilterOptions: (
      state,
      action: PayloadAction<{
        teamId: string;
        enabled: boolean;
        filterType: FilterType;
      }>
    ) => {
      const { teamId, enabled, filterType } = action.payload;
      const filter = state.teamFilterOptions[filterType];
      if (enabled) {
        if (!filter.includes(teamId)) {
          filter.push(teamId);
        }
      } else {
        state.teamFilterOptions[filterType] = filter.filter(
          (x) => x !== teamId
        );
      }

      userStorage.setItem(
        DISPATCHING_VIEW_PAGE_TEAMS_FILTER,
        JSON.stringify(state.teamFilterOptions)
      );
    },
    setCargoTagFilterIds: (state, action: PayloadAction<string[]>) => {
      state.cargoTagFilterIds = action.payload;
      userStorage.setItem(
        DISPATCHING_VIEW_PAGE_CARGO_TAG_FILTER,
        JSON.stringify(state.cargoTagFilterIds)
      );
    },
    setTeamFilterOptions: (
      state,
      action: PayloadAction<Record<FilterType, string[]>>
    ) => {
      state.teamFilterOptions = action.payload;
    },
    setAllFilterOptions: (state, action: PayloadAction<string[]>) => {
      state.teamFilterOptions.cargos = action.payload;
      state.teamFilterOptions.routes = action.payload;
    },
    addUndispatchedSort: (state, action: PayloadAction<SortProperty>) => {
      state.unassignedCargoSortingNew.push({
        property: action.payload,
        order: "ASC",
      });
      localStorage.setItem(
        UNDISPATCHED_SORTING,
        JSON.stringify(state.unassignedCargoSortingNew)
      );
    },
    removeUndispatchedSort: (state, action: PayloadAction<SortProperty>) => {
      state.unassignedCargoSortingNew = state.unassignedCargoSortingNew.filter(
        (x) => x.property !== action.payload
      );
      localStorage.setItem(
        UNDISPATCHED_SORTING,
        JSON.stringify(state.unassignedCargoSortingNew)
      );
    },
    setUnassignedCargoSearchText: (state, action: PayloadAction<string>) => {
      state.unassignedCargoSearchText = action.payload;
    },
    toggleTruckFilter: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.truckFilterIds.indexOf(id);
      if (idx === -1) {
        state.truckFilterIds.push(id);
      } else {
        state.truckFilterIds.splice(idx, 1);
      }
    },
    toggleTrailerFilter: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.trailerFilterIds.indexOf(id);
      if (idx === -1) {
        state.trailerFilterIds.push(id);
      } else {
        state.trailerFilterIds.splice(idx, 1);
      }
    },
    toggleDriverFilter: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.driverFilterIds.indexOf(id);
      if (idx === -1) {
        state.driverFilterIds.push(id);
      } else {
        state.driverFilterIds.splice(idx, 1);
      }
    },
    clearVehicleDriverFilters: (state) => {
      state.truckFilterIds = [];
      state.trailerFilterIds = [];
      state.driverFilterIds = [];
    },
    switchUndispatchedSortOrder: (state, action: PayloadAction<string>) => {
      const index = state.unassignedCargoSortingNew.findIndex(
        (x) => x.property === action.payload
      );
      if (index === -1) {
        return;
      }
      state.unassignedCargoSortingNew[index].order =
        state.unassignedCargoSortingNew[index].order === "ASC" ? "DESC" : "ASC";
      localStorage.setItem(
        UNDISPATCHED_SORTING,
        JSON.stringify(state.unassignedCargoSortingNew)
      );
    },
    update: (
      state,
      action: PayloadAction<{
        routes: Record<string, string[]>;
        cargoId: string;
        currentIndex: number;
        currentRouteId: string | null;
      }>
    ) => {
      if (!state.dragState) {
        state.dragState = {
          committing: false,
          cargoId: action.payload.cargoId,
          currentRouteId: action.payload.currentRouteId,
          sourceRouteId: action.payload.currentRouteId,
          originalIndex: action.payload.currentIndex,
          currentIndex: action.payload.currentIndex,
          routeCargoIds: {},
        };
      }
      const { currentIndex, currentRouteId } = action.payload;
      state.dragState.routeCargoIds = {
        ...state.dragState.routeCargoIds,
        ...action.payload.routes,
      };
      state.dragState.currentIndex = currentIndex;
      state.dragState.currentRouteId = currentRouteId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setFeatures, (state) => {
        if (state.unassignedCargoSortingValueSet) {
          return;
        }

        // state.unassignedCargoSortingNew = [
        //   {
        //     property: "Læssedato",
        //     order: "ASC",
        //   },
        // ];

        state.unassignedCargoSortingValueSet = true;
      })
      .addCase(clear, (state) => {
        state.teamFilterOptions.routes = [];
      })
      .addCase(loadUndispatchedCargoViews.fulfilled, (state, action) => {
        state.originalUnassignedCargoIds = action.payload
          .filter((x) => !x.assignedRouteId && !x.smartMatch)
          .map((x) => x.id);
      })
      .addCase(endDrag, (state) => {
        if (state.dragState && !state.saving) {
          delete state.dragState;
        }
      })
      .addCase(updateSmartMatch.fulfilled, (state, action) => {
        const { cargoId, smartMatch } = action.meta.arg;
        const unassigned = state.originalUnassignedCargoIds;
        if (smartMatch) {
          state.originalUnassignedCargoIds = unassigned.filter(
            (x) => x !== cargoId
          );
        } else {
          unassigned.push(cargoId);
        }
      })
      .addCase(moveCargo.pending, (state) => {
        state.saving = true;
      })
      .addCase(moveCargo.fulfilled, (state) => {
        state.saving = false;
        if (!state.dragState) {
          return;
        }
        if (!state.dragState.currentRouteId) {
          // delete state.unassignedCargoSorting.sortProperty;
          // TODO: all of this might no longer be necessary
          const undispatchedCargoIds = state.dragState.routeCargoIds[""];
          if (undispatchedCargoIds) {
            state.originalUnassignedCargoIds = undispatchedCargoIds;
          }
        }
        delete state.dragState;
      })
      // .addCase(getAllArchivedRoutes.fulfilled, (state, action) => {
      // for (const route of action.payload.routes) {
      // state.originalCargoIds[route.id] = route.cargo_order;
      // }
      // })
      .addCase(moveCargo.rejected, (state) => {
        delete state.dragState;
        state.saving = false;
      })
      .addCase(openCargoFoundDeleted, (state, action) => {
        const index = state.originalUnassignedCargoIds.indexOf(
          action.payload.cargoId
        );
        if (index !== -1) {
          state.originalUnassignedCargoIds.splice(index, 1);
        }
      })
      .addCase(deleteCargo.fulfilled, (state, action) => {
        const cargoId = action.meta.arg;
        const cargoIds = state.originalUnassignedCargoIds;
        const index = cargoIds.indexOf(cargoId);
        if (index !== -1) {
          cargoIds.splice(index, 1);
        }
      })
      .addCase(cargoViewsDecoded, (state, action) => {
        const { cargoViews, myTeams } = action.payload;
        for (const cargoView of cargoViews) {
          const { id, deleted, assignedRouteId, assignedTeamId } = cargoView;
          const index = state.originalUnassignedCargoIds.indexOf(id);
          if (
            myTeams.includes(assignedTeamId) &&
            assignedRouteId === null &&
            !deleted
          ) {
            if (index === -1) {
              state.originalUnassignedCargoIds.unshift(id);
            }
          } else if (index !== -1) {
            // Only touch the list when the cargo was in it: an update to a
            // dispatched cargo shouldn't hand selectors a new array.
            state.originalUnassignedCargoIds.splice(index, 1);
          }
        }
      });
  },
});

export const beginDragStop = createAction<{
  routeId: string | null;
  routeStopId: string;
}>(`${prefix}/begin-drag-stop`);

export const beginDragCargo =
  (input: {
    cargoId: string;
    routeId: string | null;
    routeStopId?: string;
  }): AppThunkAction =>
  (dispatch, getState) => {
    const { routeStopId, cargoId, routeId } = input;
    const sourceRoute = selectors.selectRouteCargoIds(routeId)(getState());
    const currentIndex = sourceRoute.indexOf(cargoId);
    dispatch(
      slice.actions.update({
        cargoId,
        currentRouteId: routeId,
        currentIndex,
        routes: {},
      })
    );
    if (routeStopId) {
      dispatch(beginDragStop({ routeId, routeStopId }));
    }
  };

export const {
  setRouteSearchDates,
  clearRouteSearchDates,
  setAllFilterOptions,
  setCargoTagFilterIds,
  setTeamFilterOptions,
  addUndispatchedSort,
  removeUndispatchedSort,
  switchUndispatchedSortOrder,
  setUnassignedCargoSearchText,
  toggleTruckFilter,
  toggleTrailerFilter,
  toggleDriverFilter,
  clearVehicleDriverFilters,
} = slice.actions;
export default slice.reducer;
